import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage"
import { db, storage } from "../firebase/config"

import type { Product } from "../store/slices/productsSlice"
import type { Order } from "../store/slices/ordersSlice"
import type { Category } from "../types"

// Helper function to convert Firebase Timestamp to ISO string
const convertTimestamp = <T>(data: T): T => {
  if (data instanceof Timestamp) {
    return data.toDate().toISOString() as T
  }
  if (Array.isArray(data)) {
    return data.map(convertTimestamp) as T
  }
  if (data && typeof data === 'object' && data !== null) {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([key, value]) => [key, convertTimestamp(value)])
    ) as T
  }
  return data
}

// Products Service
export const productsService = {
  // Get all products
  async getProducts(): Promise<Product[]> {
    try {
      const querySnapshot = await getDocs(collection(db, "products"))
      return querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
        id: doc.id,
          ...convertTimestamp(data),
        } as Product
      })
    } catch (error) {
      console.error("Error getting products:", error)
      throw error
    }
  },

  // Get product by ID
  async getProduct(id: string): Promise<Product | null> {
    try {
      const docRef = doc(db, "products", id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        return { 
          id: docSnap.id, 
          ...convertTimestamp(data)
        } as Product
      }
      return null
    } catch (error) {
      console.error("Error getting product:", error)
      throw error
    }
  },

  // Add new product
  async addProduct(product: Omit<Product, "id">): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "products"), {
        ...product,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding product:", error)
      throw error
    }
  },

  // Update product
  async updateProduct(id: string, product: Partial<Product>): Promise<void> {
    try {
      const docRef = doc(db, "products", id)
      await updateDoc(docRef, {
        ...product,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating product:", error)
      throw error
    }
  },

  // Delete product
  async deleteProduct(id: string): Promise<void> {
    try {
      const docRef = doc(db, "products", id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error("Error deleting product:", error)
      throw error
    }
  },

  // Search products
  async searchProducts(searchTerm: string): Promise<Product[]> {
    try {
      const products = await this.getProducts()
      return products.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase())),
      )
    } catch (error) {
      console.error("Error searching products:", error)
      throw error
    }
  },

  // Get products by category
  async getProductsByCategory(category: string): Promise<Product[]> {
    try {
      const q = query(collection(db, "products"), where("category", "==", category))
      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
        id: doc.id,
          ...convertTimestamp(data),
        } as Product
      })
    } catch (error) {
      console.error("Error getting products by category:", error)
      throw error
    }
  },

  // Bulk update products
  async bulkUpdateProducts(updates: { id: string; data: Partial<Product> }[]): Promise<void> {
    try {
      const batch = writeBatch(db)

      updates.forEach(({ id, data }) => {
        const docRef = doc(db, "products", id)
        batch.update(docRef, { ...data, updatedAt: serverTimestamp() })
      })

      await batch.commit()
    } catch (error) {
      console.error("Error bulk updating products:", error)
      throw error
    }
  },
}

// Categories Service
export const categoriesService = {
  // Get all categories
  async getCategories(): Promise<Category[]> {
    try {
      const querySnapshot = await getDocs(collection(db, "categories"))
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[]
    } catch (error) {
      console.error("Error getting categories:", error)
      throw error
    }
  },

  // Get category by ID
  async getCategory(id: string): Promise<Category | null> {
    try {
      const docRef = doc(db, "categories", id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Category
      }
      return null
    } catch (error) {
      console.error("Error getting category:", error)
      throw error
    }
  },

  // Add new category
  async addCategory(category: Omit<Category, "id">): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "categories"), {
        ...category,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding category:", error)
      throw error
    }
  },

  // Update category
  async updateCategory(id: string, category: Partial<Category>): Promise<void> {
    try {
      const docRef = doc(db, "categories", id)
      await updateDoc(docRef, {
        ...category,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating category:", error)
      throw error
    }
  },

  // Delete category
  async deleteCategory(id: string): Promise<void> {
    try {
      const docRef = doc(db, "categories", id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error("Error deleting category:", error)
      throw error
    }
  },
}

// Orders Service
export const ordersService = {
  // Get all orders
  async getOrders(): Promise<Order[]> {
    try {
      const q = query(collection(db, "orders"), orderBy("createdAt", "desc"))
      const querySnapshot = await getDocs(q)
      
      const orders = querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...convertTimestamp(data),
        } as Order
      })
      
      return orders
    } catch (error) {
      console.error("Error getting orders:", error)
      throw error
    }
  },

  // Get order by ID
  async getOrder(id: string): Promise<Order | null> {
    try {
      const docRef = doc(db, "orders", id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        return { 
          id: docSnap.id, 
          ...convertTimestamp(data)
        } as Order
      }
      return null
    } catch (error) {
      console.error("Error getting order:", error)
      throw error
    }
  },

  // Get orders by user
  async getUserOrders(userId: string): Promise<Order[]> {
    try {
      const q = query(collection(db, "orders"), where("userId", "==", userId), orderBy("createdAt", "desc"))
      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...convertTimestamp(data),
        } as Order
      })
    } catch (error) {
      console.error("Error getting user orders:", error)
      throw error
    }
  },

  // Add new order
  async addOrder(order: Omit<Order, "id">): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "orders"), {
        ...order,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding order:", error)
      throw error
    }
  },

  // Update order
  async updateOrder(id: string, order: Partial<Order>): Promise<void> {
    try {
      const docRef = doc(db, "orders", id)
      await updateDoc(docRef, {
        ...order,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating order:", error)
      throw error
    }
  },

  // Update order status
  async updateOrderStatus(id: string, status: Order["status"]): Promise<void> {
    try {
      const docRef = doc(db, "orders", id)
      await updateDoc(docRef, {
        status,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating order status:", error)
      throw error
    }
  },

  // Update payment status
  async updatePaymentStatus(id: string, paymentStatus: Order["paymentStatus"]): Promise<void> {
    try {
      const docRef = doc(db, "orders", id)
      await updateDoc(docRef, {
        paymentStatus,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating payment status:", error)
      throw error
    }
  },

  // Delete order
  async deleteOrder(id: string): Promise<void> {
    try {
      const docRef = doc(db, "orders", id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error("Error deleting order:", error)
      throw error
    }
  },
}

// Storage Service
export const storageService = {
  // Upload image
  async uploadImage(file: File, path: string): Promise<string> {
    try {
      const storageRef = ref(storage, path)
      const snapshot = await uploadBytes(storageRef, file)
      return await getDownloadURL(snapshot.ref)
    } catch (error) {
      console.error("Error uploading image:", error)
      throw error
    }
  },

  // Delete image
  async deleteImage(url: string): Promise<void> {
    try {
      const imageRef = ref(storage, url)
      await deleteObject(imageRef)
    } catch (error) {
      console.error("Error deleting image:", error)
      throw error
    }
  },

  // Upload multiple images
  async uploadMultipleImages(files: File[], basePath: string): Promise<string[]> {
    try {
      const uploadPromises = files.map(async (file, index) => {
        const path = `${basePath}/${Date.now()}_${index}_${file.name}`
        return this.uploadImage(file, path)
      })

      return await Promise.all(uploadPromises)
    } catch (error) {
      console.error("Error uploading multiple images:", error)
      throw error
    }
  },
}

// Real-time listeners
export const realtimeService = {
  // Listen to products changes
  onProductsChange(callback: (products: Product[]) => void) {
    return onSnapshot(
      collection(db, "products"),
      (snapshot) => {
        const products = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Product[]
        callback(products)
      },
      (error) => {
        console.error("Error listening to products:", error)
      },
    )
  },

  // Listen to orders changes
  onOrdersChange(callback: (orders: Order[]) => void) {
    return onSnapshot(
      query(collection(db, "orders"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const orders = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            ...convertTimestamp(data),
          } as Order
        })
        callback(orders)
      },
      (error) => {
        console.error("Error listening to orders:", error)
      },
    )
  },

  // Listen to user orders changes
  onUserOrdersChange(userId: string, callback: (orders: Order[]) => void) {
    return onSnapshot(
      query(collection(db, "orders"), where("userId", "==", userId), orderBy("createdAt", "desc")),
      (snapshot) => {
        const orders = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            ...convertTimestamp(data),
          } as Order
        })
        callback(orders)
      },
      (error) => {
        console.error("Error listening to user orders:", error)
      },
    )
  },

  // Listen to categories changes
  onCategoriesChange(callback: (categories: Category[]) => void) {
    return onSnapshot(
      collection(db, "categories"),
      (snapshot) => {
        const categories = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Category[]
        callback(categories)
      },
      (error) => {
        console.error("Error listening to categories:", error)
      },
    )
  },
}

// Analytics Service
export const analyticsService = {
  // Get dashboard stats
  async getDashboardStats() {
    try {
      const [products, orders, categories] = await Promise.all([
        this.getProductStats(),
        this.getOrderStats(),
        this.getCategoryStats(),
      ])

      return {
        products,
        orders,
        categories,
      }
    } catch (error) {
      console.error("Error getting dashboard stats:", error)
      throw error
    }
  },

  // Get product statistics
  async getProductStats() {
    try {
      const products = await productsService.getProducts()

      return {
        total: products.length,
        inStock: products.filter((p) => p.stock > 0).length,
        outOfStock: products.filter((p) => p.stock === 0).length,
        lowStock: products.filter((p) => p.stock > 0 && p.stock < 5).length,
        featured: products.filter((p) => p.isFeatured).length,
        newArrivals: products.filter((p) => p.isNewArrival).length,
        bestSellers: products.filter((p) => p.isBestSeller).length,
      }
    } catch (error) {
      console.error("Error getting product stats:", error)
      throw error
    }
  },

  // Get order statistics
  async getOrderStats() {
    try {
      const orders = await ordersService.getOrders()

      const totalRevenue = orders
        .filter((o) => o.paymentStatus === "completed")
        .reduce((sum, order) => sum + order.total, 0)

      return {
        total: orders.length,
        pending: orders.filter((o) => o.status === "pending").length,
        confirmed: orders.filter((o) => o.status === "confirmed").length,
        shipped: orders.filter((o) => o.status === "shipped").length,
        delivered: orders.filter((o) => o.status === "delivered").length,
        cancelled: orders.filter((o) => o.status === "cancelled").length,
        totalRevenue,
        averageOrderValue: orders.length > 0 ? totalRevenue / orders.length : 0,
      }
    } catch (error) {
      console.error("Error getting order stats:", error)
      throw error
    }
  },

  // Get category statistics
  async getCategoryStats() {
    try {
      const categories = await categoriesService.getCategories()
      const products = await productsService.getProducts()

      const categoryStats = categories.map((category) => ({
        id: category.id,
        name: category.name,
        productCount: products.filter((p) => p.category === category.slug).length,
      }))

      return {
        total: categories.length,
        categoryStats,
      }
    } catch (error) {
      console.error("Error getting category stats:", error)
      throw error
    }
  },
}
